import { useCallback, useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import AuthContext from "./AuthContext";

import {
  saveToken,
  saveRefreshToken,
  saveUser,
  clearAuthStorage,
  getToken,
  getUser,
} from "../../utils/authStorage";

import { loginService } from "../../services/authService";

import {
  fetchTenantProfile,
  clearTenantProfile,
} from "../../store/tenantProfile/tenantProfileSlice";

/*
|--------------------------------------------------------------------------
| NORMALIZE CODE
|--------------------------------------------------------------------------
*/

const normalizeCode = (value) => {
  return String(value || "")
    .trim()
    .toUpperCase();
};

/*
|--------------------------------------------------------------------------
| NORMALIZE PERMISSION
|--------------------------------------------------------------------------
*/

const normalizePermission = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "object") {
    return String(value?.code || "")
      .trim()
      .toLowerCase();
  }

  return String(value).trim().toLowerCase();
};

/*
|--------------------------------------------------------------------------
| GET ERROR STATUS
|--------------------------------------------------------------------------
*/

const getErrorStatus = (error) => {
  return Number(
    error?.statusCode ||
      error?.code ||
      error?.status ||
      error?.response?.status ||
      error?.response?.code ||
      0,
  );
};

/*
|--------------------------------------------------------------------------
| AUTH PROVIDER
|--------------------------------------------------------------------------
*/

const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();

  /*
        |--------------------------------------------------------------------------
        | AUTH STATE
        |--------------------------------------------------------------------------
        */

  const [user, setUser] = useState(null);

  const [token, setToken] = useState(null);

  const [loading, setLoading] = useState(true);

  const [isAuthenticated, setIsAuthenticated] = useState(false);

  /*
        |--------------------------------------------------------------------------
        | TENANT PROFILE REDUX
        |--------------------------------------------------------------------------
        |
        | If your slice uses a different selector structure, this safely falls
        | back to values stored inside user.
        |
        |--------------------------------------------------------------------------
        */

  const tenantProfileState = useSelector((state) => state?.tenantProfile || {});

  /*
        |--------------------------------------------------------------------------
        | NORMALIZED TENANT PROFILE DATA
        |--------------------------------------------------------------------------
        |
        | tenantProfileSlice stores the current profile at the TOP LEVEL:
        |
        | state.tenantProfile.user
        | state.tenantProfile.role
        | state.tenantProfile.tenant
        | state.tenantProfile.permissions
        |
        | Older versions may have used:
        |
        | state.tenantProfile.data
        | state.tenantProfile.profile
        |
        | Support both shapes so AuthContext always receives the fresh /auth/me
        | permission list.
        |
        |--------------------------------------------------------------------------
        */

  const tenantProfileData = useMemo(() => {
    const nestedProfile =
      tenantProfileState?.data || tenantProfileState?.profile || null;

    if (nestedProfile) {
      return nestedProfile;
    }

    const hasTopLevelProfile =
      Boolean(tenantProfileState?.user) ||
      Boolean(tenantProfileState?.role) ||
      Boolean(tenantProfileState?.tenant) ||
      Array.isArray(tenantProfileState?.permissions) ||
      Boolean(tenantProfileState?.initialized);

    if (!hasTopLevelProfile) {
      return null;
    }

    return {
      user: tenantProfileState?.user || null,

      role: tenantProfileState?.role || null,

      tenant: tenantProfileState?.tenant || null,

      permissions: Array.isArray(tenantProfileState?.permissions)
        ? tenantProfileState.permissions
        : [],

      permissionDetails: Array.isArray(tenantProfileState?.permissionDetails)
        ? tenantProfileState.permissionDetails
        : [],
    };
  }, [tenantProfileState]);

  /*
        |--------------------------------------------------------------------------
        | CLEAR SESSION
        |--------------------------------------------------------------------------
        |
        | Single source for:
        |
        | manual logout
        | expired token
        | invalid session during refresh
        |
        |--------------------------------------------------------------------------
        */

  const clearSession = useCallback(() => {
    /*
                    |--------------------------------------------------------------------------
                    | CONTEXT
                    |--------------------------------------------------------------------------
                    */

    setUser(null);

    setToken(null);

    setIsAuthenticated(false);

    /*
                    |--------------------------------------------------------------------------
                    | STORAGE
                    |--------------------------------------------------------------------------
                    */

    clearAuthStorage();

    /*
                    |--------------------------------------------------------------------------
                    | REDUX
                    |--------------------------------------------------------------------------
                    */

    dispatch(clearTenantProfile());
  }, [dispatch]);

  /*
        |--------------------------------------------------------------------------
        | BUILD USER WITH TENANT CONTEXT
        |--------------------------------------------------------------------------
        */

  const buildUserWithProfile = useCallback((baseUser, profile) => {
    if (!profile) {
      return baseUser || null;
    }

    return {
      ...(baseUser || {}),

      ...(profile?.user || {}),

      role: profile?.role || baseUser?.role || null,

      tenant: profile?.tenant || baseUser?.tenant || null,
    };
  }, []);

  /*
        |--------------------------------------------------------------------------
        | LOAD TENANT PROFILE
        |--------------------------------------------------------------------------
        */

  const loadTenantProfile = useCallback(async () => {
    try {
      const result = await dispatch(fetchTenantProfile()).unwrap();

      /*
                        |--------------------------------------------------------------------------
                        | MERGE USER
                        |--------------------------------------------------------------------------
                        */

      if (result?.user) {
        setUser((currentUser) => {
          const updatedUser = buildUserWithProfile(currentUser, result);

          saveUser(updatedUser);

          return updatedUser;
        });
      }

      return {
        success: true,

        data: result,
      };
    } catch (error) {
      console.error("[AuthProvider] Unable to load tenant profile:", error);

      return {
        success: false,

        error,
      };
    }
  }, [dispatch, buildUserWithProfile]);

  /*
        |--------------------------------------------------------------------------
        | RESTORE AUTH SESSION
        |--------------------------------------------------------------------------
        |
        | Browser refresh:
        |
        | local token
        |      ↓
        | restore temporary auth state
        |      ↓
        | call /v1/admin/auth/me
        |      ↓
        | 200 -> refresh current user/role/permissions
        |
        | 401 -> clear session
        |
        | 403 -> DO NOT logout automatically
        |
        |--------------------------------------------------------------------------
        */

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        setLoading(true);

        /*
                            |--------------------------------------------------------------------------
                            | LOCAL SESSION
                            |--------------------------------------------------------------------------
                            */

        const storedToken = getToken();

        const storedUser = getUser();

        /*
                            |--------------------------------------------------------------------------
                            | NO TOKEN
                            |--------------------------------------------------------------------------
                            */

        if (!storedToken) {
          if (mounted) {
            setToken(null);

            setUser(null);

            setIsAuthenticated(false);
          }

          dispatch(clearTenantProfile());

          return;
        }

        /*
                            |--------------------------------------------------------------------------
                            | TEMPORARILY RESTORE
                            |--------------------------------------------------------------------------
                            |
                            | AuthGuard loading remains true until /me validation
                            | finishes.
                            |
                            |--------------------------------------------------------------------------
                            */

        if (mounted) {
          setToken(storedToken);

          setUser(storedUser || null);

          setIsAuthenticated(true);
        }

        /*
                            |--------------------------------------------------------------------------
                            | VERIFY CURRENT SESSION
                            |--------------------------------------------------------------------------
                            */

        try {
          const result = await dispatch(fetchTenantProfile()).unwrap();

          if (!mounted) {
            return;
          }

          /*
                                |--------------------------------------------------------------------------
                                | UPDATE USER
                                |--------------------------------------------------------------------------
                                */

          if (result?.user) {
            const updatedUser = buildUserWithProfile(storedUser, result);

            setUser(updatedUser);

            saveUser(updatedUser);
          }

          setIsAuthenticated(true);
        } catch (error) {
          /*
                                |--------------------------------------------------------------------------
                                | SESSION INVALID / EXPIRED
                                |--------------------------------------------------------------------------
                                */

          const statusCode = getErrorStatus(error);

          /*
                                |--------------------------------------------------------------------------
                                | ONLY 401 = LOGOUT
                                |--------------------------------------------------------------------------
                                |
                                | 403 means user is authenticated but forbidden.
                                |
                                |--------------------------------------------------------------------------
                                */

          if (statusCode === 401) {
            clearAuthStorage();

            dispatch(clearTenantProfile());

            if (mounted) {
              setToken(null);

              setUser(null);

              setIsAuthenticated(false);
            }

            return;
          }

          /*
                                |--------------------------------------------------------------------------
                                | NON-AUTH FAILURE
                                |--------------------------------------------------------------------------
                                */

          console.error(
            "[AuthProvider] Tenant profile initialization failed:",
            error,
          );
        }
      } catch (error) {
        console.error(
          "[AuthProvider] Authentication initialization failed:",
          error,
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
    };
  }, [dispatch, buildUserWithProfile]);

  /*
        |--------------------------------------------------------------------------
        | GLOBAL TENANT SESSION EXPIRED EVENT
        |--------------------------------------------------------------------------
        |
        | apiCalling.js dispatches:
        |
        | comkwik:tenant-session-expired
        |
        | when an authenticated tenant API returns HTTP 401.
        |
        |--------------------------------------------------------------------------
        */

  useEffect(() => {
    const handleSessionExpired = (event) => {
      console.warn(
        "[AuthProvider] Tenant session expired:",
        event?.detail || {},
      );

      clearSession();
    };

    window.addEventListener(
      "comkwik:tenant-session-expired",
      handleSessionExpired,
    );

    return () => {
      window.removeEventListener(
        "comkwik:tenant-session-expired",
        handleSessionExpired,
      );
    };
  }, [clearSession]);

  /*
        |--------------------------------------------------------------------------
        | LOGIN
        |--------------------------------------------------------------------------
        */

  const login = async (payload) => {
    try {
      setLoading(true);

      /*
                    |--------------------------------------------------------------------------
                    | LOGIN REQUEST
                    |--------------------------------------------------------------------------
                    */

      const response = await loginService(payload);

      /*
                    |--------------------------------------------------------------------------
                    | LOGIN FAILED
                    |--------------------------------------------------------------------------
                    */

      if (response?.status !== "success" && response?.success !== true) {
        return response;
      }

      /*
                    |--------------------------------------------------------------------------
                    | RESPONSE DATA
                    |--------------------------------------------------------------------------
                    */

      const accessToken = response?.data?.accessToken;

      const refreshToken = response?.data?.refreshToken;

      const userData = response?.data?.user;

      /*
                    |--------------------------------------------------------------------------
                    | TOKEN REQUIRED
                    |--------------------------------------------------------------------------
                    */

      if (!accessToken) {
        return {
          success: false,

          message: "Access token was not returned by the server.",
        };
      }

      /*
                    |--------------------------------------------------------------------------
                    | SAVE STORAGE
                    |--------------------------------------------------------------------------
                    */

      saveToken(accessToken);

      if (refreshToken) {
        saveRefreshToken(refreshToken);
      }

      if (userData) {
        saveUser(userData);
      }

      /*
                    |--------------------------------------------------------------------------
                    | UPDATE CONTEXT
                    |--------------------------------------------------------------------------
                    */

      setToken(accessToken);

      setUser(userData || null);

      setIsAuthenticated(true);

      /*
                    |--------------------------------------------------------------------------
                    | LOAD FRESH PROFILE
                    |--------------------------------------------------------------------------
                    */

      let tenantProfile = null;

      try {
        tenantProfile = await dispatch(fetchTenantProfile()).unwrap();

        if (tenantProfile?.user) {
          const updatedUser = buildUserWithProfile(userData, tenantProfile);

          setUser(updatedUser);

          saveUser(updatedUser);
        }
      } catch (profileError) {
        console.error(
          "[AuthProvider] Login succeeded but tenant profile failed:",
          profileError,
        );

        const statusCode = getErrorStatus(profileError);

        /*
                        |--------------------------------------------------------------------------
                        | ONLY 401 INVALIDATES LOGIN
                        |--------------------------------------------------------------------------
                        */

        if (statusCode === 401) {
          clearSession();

          return {
            success: false,

            message:
              profileError?.message || "Unable to verify the business session.",
          };
        }
      }

      /*
                    |--------------------------------------------------------------------------
                    | SUCCESS
                    |--------------------------------------------------------------------------
                    */

      return {
        success: true,

        user: tenantProfile?.user || userData,

        tenant: tenantProfile?.tenant || null,

        role: tenantProfile?.role || null,

        permissions: tenantProfile?.permissions || [],
      };
    } catch (error) {
      console.error("[AuthProvider] Login error:", error);

      return {
        success: false,

        message: error?.response?.message || error?.message || "Login failed.",
      };
    } finally {
      setLoading(false);
    }
  };

  /*
        |--------------------------------------------------------------------------
        | REFRESH CURRENT TENANT PROFILE
        |--------------------------------------------------------------------------
        |
        | Useful after:
        |
        | role update
        | permission update
        | employee permission update
        | tenant profile update
        |
        |--------------------------------------------------------------------------
        */

  const refreshTenantProfile = async () => {
    if (!getToken()) {
      return {
        success: false,

        message: "Authentication is required.",
      };
    }

    const result = await loadTenantProfile();

    /*
                |--------------------------------------------------------------------------
                | SESSION EXPIRED DURING MANUAL REFRESH
                |--------------------------------------------------------------------------
                */

    if (result?.success === false) {
      const statusCode = getErrorStatus(result?.error);

      if (statusCode === 401) {
        clearSession();
      }
    }

    return result;
  };

  /*
        |--------------------------------------------------------------------------
        | LOGOUT
        |--------------------------------------------------------------------------
        */

  const logout = () => {
    clearSession();

    return {
      success: true,
    };
  };

  /*
        |--------------------------------------------------------------------------
        | DERIVED ROLE
        |--------------------------------------------------------------------------
        */

  const role = tenantProfileData?.role || user?.role || null;

  /*
        |--------------------------------------------------------------------------
        | DERIVED TENANT
        |--------------------------------------------------------------------------
        */

  const tenant = tenantProfileData?.tenant || user?.tenant || null;

  const permissions = useMemo(() => {
    const profilePermissions = tenantProfileData?.permissions;

    /*
            |--------------------------------------------------------------------------
            | FRESH /AUTH/ME PERMISSIONS
            |--------------------------------------------------------------------------
            */

    if (Array.isArray(profilePermissions)) {
      return profilePermissions;
    }

    /*
            |--------------------------------------------------------------------------
            | FALLBACK USER PERMISSIONS
            |--------------------------------------------------------------------------
            */

    const userPermissions = user?.permissions;

    if (Array.isArray(userPermissions)) {
      return userPermissions;
    }

    return [];
  }, [tenantProfileData, user]);

  /*
|--------------------------------------------------------------------------
| PERMISSION CODES
|--------------------------------------------------------------------------
|
| AuthGuard should use this normalized list.
|
|--------------------------------------------------------------------------
*/

  const permissionCodes = useMemo(() => {
    return [...new Set(permissions.map(normalizePermission).filter(Boolean))];
  }, [permissions]);

  /*
        |--------------------------------------------------------------------------
        | OWNER
        |--------------------------------------------------------------------------
        */

  const isOwner = useMemo(() => {
    return (
      normalizeCode(user?.userType) === "OWNER" ||
      normalizeCode(role?.code) === "OWNER" ||
      normalizeCode(user?.role?.code) === "OWNER" ||
      normalizeCode(user?.roleCode) === "OWNER"
    );
  }, [user, role]);

  /*
        |--------------------------------------------------------------------------
        | CONTEXT VALUE
        |--------------------------------------------------------------------------
        */

  const contextValue = useMemo(
    () => ({
      /*
                    |--------------------------------------------------------------------------
                    | AUTH
                    |--------------------------------------------------------------------------
                    */

      user,

      token,

      loading,

      isAuthenticated,

      /*
                    |--------------------------------------------------------------------------
                    | TENANT CONTEXT
                    |--------------------------------------------------------------------------
                    */

      tenant,

      role,

      permissions,

      permissionCodes,

      isOwner,

      /*
                    |--------------------------------------------------------------------------
                    | ACTIONS
                    |--------------------------------------------------------------------------
                    */

      login,

      logout,

      refreshTenantProfile,

      clearSession,

      /*
                    |--------------------------------------------------------------------------
                    | EXISTING SETTERS
                    |--------------------------------------------------------------------------
                    */

      setUser,

      setToken,

      setLoading,
    }),
    [
      user,
      token,
      loading,
      isAuthenticated,
      tenant,
      role,
      permissions,
      permissionCodes,
      isOwner,
      clearSession,
    ],
  );

  /*
        |--------------------------------------------------------------------------
        | PROVIDER
        |--------------------------------------------------------------------------
        */

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthProvider;
